/**
 * Manual add — for whatever the feeds missed.
 *
 * A URL is still the only thing she has to type. What changed on 22 August 2026 is what
 * happens next: the page is **read before the item is created**, so the dialog fills itself
 * in from the article — its title, the date it was actually published, a summary, the clients
 * it bears on, and any deadline it states. She corrects what is wrong and presses Add.
 *
 * Three defects made that necessary, and they were all invisible rather than loud:
 *
 * * the item was dated **today**, which is the one date it certainly does not have — an item
 *   added by hand is one the feeds missed, so it is usually days or weeks old;
 * * no summary was ever drafted, because a manual add arrives `tracked` and the stream
 *   enricher only ever looked at `new`. A blank summary reads as a model with nothing to say
 *   rather than a model that was never asked;
 * * a deadline in the publication reached nothing, so an item added *because* of its deadline
 *   went into the log with no action against it.
 *
 * **Reading first is what lets the deadline become an action without breaking the rule that
 * suggestions never auto-apply.** The date is offered with the words it was read out of, and
 * it becomes an action because she looked at it and pressed a button — not because the system
 * decided while she was elsewhere.
 *
 * The reading is deliberately not automatic on every keystroke: it costs a model call, so it
 * fires when she has finished with the URL box, and once per URL.
 *
 * **Since 20 October 2026 an added item goes to the review queue, not the log** — Craig: a
 * hand-added item is read and decided on like any imported one. So this dialog now asks only
 * for what identifies the item (address, title, date), shows the summary it drafted so she
 * can see the page was read, and leaves summary, clients, next steps and deadline to the
 * queue, where Track and Track + action already do them. The drafted summary, tags and any
 * deadline travel with the item as suggestions (`ai_*`, cached server-side by the preview),
 * which is exactly how an imported item arrives. The server refuses a `status` at creation,
 * so nothing sent from here can skip the queue.
 */

import { useState } from "react";

import { api } from "../api";
import type { Item, ItemPreview } from "../types";

interface Props {
  onClose: () => void;
  onAdded: (item: Item) => void;
  onError: (error: unknown) => void;
}

export function ManualAddDialog({ onClose, onAdded, onError }: Props) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [published, setPublished] = useState("");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState<ItemPreview | null>(null);
  /** The URL the current reading belongs to, so editing it back does not re-read. */
  const [readUrl, setReadUrl] = useState("");
  const [existing, setExisting] = useState<Item | null>(null);

  const trimmed = url.trim();
  const usable = trimmed.startsWith("http");

  /**
   * Read the page, once per URL.
   *
   * Everything it returns is a *default*, never an overwrite: if she has already typed a
   * title or a date, the reading arriving a second later must not take it away from
   * her. That is why each field is only filled when it is still empty.
   */
  const readPage = async () => {
    if (!usable || reading || trimmed === readUrl) return;
    setReading(true);
    setReadUrl(trimmed);
    try {
      const preview = await api.previewItem(trimmed);
      if (preview.already_existed && preview.item) {
        setExisting(preview.item);
        return;
      }
      setRead(preview);
      setTitle((current) => current || preview.title);
      setPublished((current) => current || preview.published_at);
    } catch (error) {
      onError(error);
      // Cleared so she can try again by leaving the box, rather than being stuck with a
      // dialog that has quietly decided this URL is unreadable.
      setReadUrl("");
    } finally {
      setReading(false);
    }
  };

  const add = async () => {
    setBusy(true);
    try {
      // No status: the server files every manual add as `new`, in the review queue.
      const item = await api.addItem(trimmed, {
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(published ? { published_at: published } : {}),
      });
      if (item.already_existed) {
        setExisting(item);
        return;
      }
      onAdded(item);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  if (existing) {
    return (
      <div className="overlay" onClick={onClose}>
        <div className="dialog" onClick={(event) => event.stopPropagation()}>
          <h2>Add an item</h2>
          <p>
            We already have that one — it came in from <strong>{existing.source_name}</strong> and
            is currently <strong>{existing.status}</strong>.
          </p>
          <p className="muted">{existing.title}</p>
          <div className="row">
            <button className="primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="dialog" onClick={(event) => event.stopPropagation()}>
        <h2>Add an item</h2>

        <label>
          URL
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            onBlur={readPage}
            onKeyDown={(event) => {
              if (event.key === "Enter") readPage();
            }}
            placeholder="https://www.fca.org.uk/publication/…"
            autoFocus
          />
        </label>

        {reading ? (
          <p className="muted small">Reading the page…</p>
        ) : usable && trimmed !== readUrl ? (
          <p className="muted small">
            <button className="link" onClick={readPage}>
              Read the page
            </button>{" "}
            — fills in the title and date, and drafts a summary.
          </p>
        ) : null}

        {read?.notes.map((note) => (
          <p className="small warn" key={note}>
            {note}
          </p>
        ))}

        <label>
          {/*
            The fallback is deliberately described rather than computed here. `derive_title`
            is one function in `reghorizon.normalise`, and a second copy in TypeScript would
            be a second answer to the same question — the mistake `statusForNextStep` exists
            to avoid. So the field says what will happen and the server decides it.
          */}
          Title{" "}
          <span className="muted small">
            — left blank, we read it from the page, or name it from the address
          </span>
          <input
            value={title}
            placeholder="named from the page or the address"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label>
          Date <span className="muted small">— when the page was last updated</span>
          <input
            type="date"
            value={published}
            onChange={(event) => setPublished(event.target.value)}
          />
        </label>
        {/* The provenance, not decoration: she is accepting this date in a second, and one
            with no visible origin is one she has to go and check for herself. The basis leads
            with which date it is — "last updated, per schema.org dateModified: 10/07/2026" —
            because whether this is the document's most recent state is the whole question.
            When she wants to know when it first appeared, the item links to the article. */}
        {published && read?.published_basis && published === read.published_at ? (
          <p className="muted small">Read from the page — {read.published_basis}</p>
        ) : !published ? (
          <p className="muted small">Left blank, today's date is used.</p>
        ) : null}

        {/* Read-only on purpose: she decides about it in the review queue, beside every other
            new item. Shown here so a PDF or an awkward page visibly *was* read. */}
        {read?.enriched && read.summary ? (
          <div className="small">
            <p className="muted">Drafted summary — you will review it in the queue:</p>
            <p>{read.summary}</p>
          </div>
        ) : null}

        <div className="row">
          <button className="primary" disabled={busy || reading || !usable} onClick={add}>
            {busy ? "Adding…" : "Add to the review queue"}
          </button>
          <button onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
